import React from "react";
import { Tooltip, Box, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import Text from "./Text";
import { DEFAULT_KEY_LABEL_COLOR } from "../utils/utils";

interface CustomTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: TooltipProps["placement"];
  delay?: number;
  disableTooltip?: boolean;
  width?: string;
}

// Styled tooltip using MUI's styled API
const StyledTooltip = styled(Tooltip)(() => ({
  "& .MuiTooltip-tooltip": {
    background: "white",
    padding: "12px 16px",
    borderRadius: "8px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    maxWidth: "300px",
    lineHeight: 1.4,
    border: "1px solid #e0e0e0",
  },
}));

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  children,
  content,
  position = "top",
  delay = 300,
  disableTooltip = false,
  width = "300px",
}) => {
  // If tooltip is disabled, just render the children without the tooltip wrapper
  if (disableTooltip) {
    return (
      <Box
        sx={{
          display: "inline-block",
          width: "100%",
          cursor: "pointer",
          transition: "transform 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <StyledTooltip
      title={<Box>{content}</Box>}
      placement={position}
      arrow
      enterDelay={delay}
      leaveDelay={0}
      slotProps={{
        popper: {
          sx: {
            "& .MuiTooltip-tooltip": {
              padding: 0,
              background: "white",
              maxWidth: width,
              border: "1px solid #e0e0e0",
              lineHeight: 1.4,
              boxShadow:
                "0 4px 8px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
              "& .MuiTooltip-arrow": {
                color: "white",
              },
            },
          },
        },
      }}
    >
      <Box
        sx={{
          display: "inline-block",
          width: "100%",
          cursor: "pointer",
          transition: "transform 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        }}
      >
        {children}
      </Box>
    </StyledTooltip>
  );
};

// Helper Row component for label/value layout
export const Row: React.FC<{
  label: string;
  value: React.ReactNode;
  multiline?: boolean;
}> = ({ label, value, multiline = false }) => (
  <Box
    sx={{ display: "flex", alignItems: multiline ? "flex-start" : "center" }}
  >
    <Text sx={{ minWidth: 100, color: DEFAULT_KEY_LABEL_COLOR }}>{label}</Text>
    <Text sx={{ margin: "0 4px" }}>:</Text>
    <Text
      sx={{
        color: "#222",
        wordBreak: "break-word",
        whiteSpace: multiline ? "pre-line" : "normal",
      }}
    >
      : &nbsp; {value}
    </Text>
  </Box>
);

export default CustomTooltip;
